import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { approvalApi, holidayApi } from '@/services/api';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday, isWeekend, addMonths, subMonths, isSameDay } from 'date-fns';
import { ChevronLeft, ChevronRight, Users, Calendar as CalendarIcon, UserCheck, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore } from '@/store/themeStore';

export default function TeamCalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const { user } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const isManager = ['MANAGER', 'SENIOR_MANAGER', 'MD', 'ORG_ADMIN', 'HR_MANAGER'].includes(user?.role || '') || !!user?.hasDirectReports;

  const startDate = startOfMonth(currentDate);
  const endDate = endOfMonth(currentDate);

  const { data: leaves = [], isLoading } = useQuery({
    queryKey: ['team-calendar', format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd')],
    queryFn: async () => {
      const res = await approvalApi.getTeamCalendar({
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear(),
      });
      return res.data.data;
    },
  });

  const { data: holidays = [] } = useQuery({
    queryKey: ['holidays', currentDate.getFullYear()],
    queryFn: async () => {
      const res = await holidayApi.getAll(currentDate.getFullYear());
      return res.data.data;
    },
  });

  const daysInMonth = eachDayOfInterval({ start: startDate, end: endDate });

  const getLeavesForDay = (date: Date) =>
    isWeekend(date) || getHolidaysForDay(date).length > 0
      ? []
      : leaves.filter((leave: any) => {
      const from = new Date(leave.fromDate);
      const to = new Date(leave.toDate);
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return d >= new Date(from.getFullYear(), from.getMonth(), from.getDate()) &&
             d <= new Date(to.getFullYear(), to.getMonth(), to.getDate());
      });

  const getHolidaysForDay = (date: Date) =>
    holidays.filter((h: any) => {
      const hd = new Date(h.date);
      return hd.getFullYear() === date.getFullYear() &&
        hd.getMonth() === date.getMonth() &&
        hd.getDate() === date.getDate();
    });

  const selectedLeaves = selectedDate ? getLeavesForDay(selectedDate) : [];
  const selectedHolidays = selectedDate ? getHolidaysForDay(selectedDate) : [];

  const changeMonth = (nextDate: Date) => {
    setCurrentDate(nextDate);
    setSelectedDate(startOfMonth(nextDate));
  };

  const prevMonth = () => changeMonth(subMonths(currentDate, 1));
  const nextMonth = () => changeMonth(addMonths(currentDate, 1));

  // Status badge styling
  const getStatusStyle = (status: string) => {
    if (status === 'APPROVED') return {
      bg: isDark ? 'rgba(22,163,74,0.15)' : '#dcfce7',
      text: isDark ? '#4ade80' : '#15803d',
      border: isDark ? 'rgba(22,163,74,0.3)' : '#bbf7d0',
      icon: <CheckCircle2 className="w-3 h-3" />,
    };
    if (status === 'PENDING') return {
      bg: isDark ? 'rgba(234,179,8,0.15)' : '#fef9c3',
      text: isDark ? '#facc15' : '#854d0e',
      border: isDark ? 'rgba(234,179,8,0.3)' : '#fde68a',
      icon: <Clock className="w-3 h-3" />,
    };
    return {
      bg: isDark ? 'rgba(100,116,139,0.15)' : '#f1f5f9',
      text: isDark ? '#94a3b8' : '#475569',
      border: isDark ? '#334155' : '#e2e8f0',
      icon: null,
    };
  };

  // Calendar cell styling
  const getCellStyle = (isSelected: boolean, isHoliday: boolean, isWknd: boolean) => {
    if (isSelected) return isDark
      ? { backgroundColor: 'rgba(59,130,246,0.18)', outline: '2px solid rgba(96,165,250,0.6)', outlineOffset: '-2px' }
      : { backgroundColor: '#eff6ff', outline: '2px solid #60a5fa', outlineOffset: '-2px' };
    if (isHoliday) return isDark
      ? { backgroundColor: 'rgba(239,68,68,0.08)' }
      : { backgroundColor: '#fef2f2' };
    if (isWknd) return isDark
      ? { backgroundColor: 'rgba(255,255,255,0.02)' }
      : { backgroundColor: '#f8fafc' };
    return {};
  };

  return (
    <div className="space-y-6">
      {/* Month navigator */}
      <div className="flex justify-end">
        <div className="relative">
          <div
            className="flex items-center justify-between w-[220px] px-2 py-2 rounded-xl border"
          style={{
            backgroundColor: 'var(--card-bg, #ffffff)',
            borderColor: 'var(--card-border, #e2e8f0)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          <Button variant="ghost" size="sm" onClick={prevMonth} className="w-9 h-9 p-0 shrink-0">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <button
            type="button"
            onClick={() => setShowMonthPicker(value => !value)}
            className="font-semibold text-slate-700 w-[140px] text-center"
          >
            {format(currentDate, 'MMMM yyyy')}
          </button>
          <Button variant="ghost" size="sm" onClick={nextMonth} className="w-9 h-9 p-0 shrink-0">
            <ChevronRight className="w-5 h-5" />
          </Button>
          </div>
          {showMonthPicker && (
            <div
              className="absolute right-0 top-full z-20 mt-2 w-[220px] rounded-xl border p-3 shadow-lg"
              style={{
                backgroundColor: 'var(--card-bg, #ffffff)',
                borderColor: 'var(--card-border, #e2e8f0)',
              }}
            >
              <div className="grid grid-cols-3 gap-1">
                {Array.from({ length: 12 }, (_, month) => {
                  const monthDate = new Date(currentDate.getFullYear(), month, 1);
                  const isCurrentMonth = month === currentDate.getMonth();
                  return (
                    <button
                      key={month}
                      type="button"
                      onClick={() => {
                        changeMonth(monthDate);
                        setShowMonthPicker(false);
                      }}
                      className="rounded-lg px-2 py-2 text-xs font-medium"
                      style={{
                        backgroundColor: isCurrentMonth ? (isDark ? 'rgba(59,130,246,0.2)' : '#dbeafe') : 'transparent',
                        color: isCurrentMonth ? (isDark ? '#93c5fd' : '#1d4ed8') : (isDark ? '#cbd5e1' : '#475569'),
                      }}
                    >
                      {format(monthDate, 'MMM')}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Calendar Grid */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: 'var(--card-bg, #ffffff)',
          border: `1px solid var(--card-border, #e2e8f0)`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}
      >
        {/* Day headers */}
        <div
          className="grid grid-cols-7 border-b"
          style={{ borderColor: 'var(--card-border, #e2e8f0)', backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}
        >
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
            <div
              key={day}
              className={`py-2.5 text-center text-xs font-semibold uppercase tracking-wider ${i === 0 || i === 6 ? 'text-red-400' : 'text-slate-500'}`}
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 auto-rows-[72px]">
          {Array.from({ length: startDate.getDay() }).map((_, i) => (
            <div
              key={`es-${i}`}
              className="border-b border-r"
              style={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9', backgroundColor: isDark ? 'rgba(255,255,255,0.01)' : '#fafafa' }}
            />
          ))}

          {daysInMonth.map((date) => {
            const dayLeaves = getLeavesForDay(date);
            const dayHolidays = getHolidaysForDay(date);
            const isWknd = isWeekend(date);
            const isTdy = isToday(date);
            const isHoliday = dayHolidays.length > 0;
            const isSelected = selectedDate && isSameDay(date, selectedDate);
            const cellStyle = getCellStyle(!!isSelected, isHoliday, isWknd);

            return (
              <div
                key={date.toISOString()}
                onClick={() => setSelectedDate(date)}
                className="border-b border-r p-1.5 flex flex-col cursor-pointer transition-all duration-150 hover:brightness-95"
                style={{
                  borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
                  ...cellStyle,
                }}
              >
                <div className="flex justify-between items-center">
                  <span
                    className="text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full transition-colors"
                    style={isTdy
                      ? { backgroundColor: '#3b82f6', color: '#ffffff' }
                      : { color: isWknd ? (isDark ? '#f87171' : '#ef4444') : (isDark ? '#cbd5e1' : '#374151') }
                    }
                  >
                    {format(date, 'd')}
                  </span>
                  {dayLeaves.length > 0 && (
                    <span
                      className="text-[10px] font-bold flex items-center gap-0.5 px-1 py-0.5 rounded"
                      style={{
                        backgroundColor: isDark ? 'rgba(59,130,246,0.2)' : '#dbeafe',
                        color: isDark ? '#93c5fd' : '#1d4ed8',
                      }}
                    >
                      <Users className="w-2.5 h-2.5" /> {dayLeaves.length}
                    </span>
                  )}
                </div>

                {isHoliday && (
                  <div className="mt-0.5 text-[9px] truncate font-semibold" style={{ color: isDark ? '#f87171' : '#dc2626' }}>
                    🎉 {dayHolidays[0].name}
                  </div>
                )}

                {dayLeaves.length > 0 && (
                  <div className="flex flex-wrap gap-0.5 mt-1">
                    {dayLeaves.slice(0, 6).map((leave: any) => {
                      const raw = leave.leaveType?.colour ?? '#6366f1';
                      const colour = raw === '#000000' || raw === '#000' ? '#6366f1' : raw;
                      return (
                        <span
                          key={leave.id}
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: colour }}
                          title={`${leave.user.name} – ${leave.leaveType?.name}`}
                        />
                      );
                    })}
                    {dayLeaves.length > 6 && (
                      <span className="text-[9px]" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>+{dayLeaves.length - 6}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {Array.from({ length: 6 - endDate.getDay() }).map((_, i) => (
            <div
              key={`ee-${i}`}
              className="border-b border-r"
              style={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9', backgroundColor: isDark ? 'rgba(255,255,255,0.01)' : '#fafafa' }}
            />
          ))}
        </div>
      </div>

      {/* Detail Panel */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: 'var(--card-bg, #ffffff)',
          border: `1px solid var(--card-border, #e2e8f0)`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}
      >
        {/* Panel header */}
        <div
          className="px-5 py-4 flex items-center gap-3 border-b"
          style={{ borderColor: 'var(--card-border, #f1f5f9)' }}
        >
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: isDark ? 'rgba(59,130,246,0.15)' : '#dbeafe' }}
          >
            <CalendarIcon className="w-4 h-4" style={{ color: isDark ? '#60a5fa' : '#2563eb' }} />
          </div>
          <div>
            <span className="font-semibold text-slate-700 text-sm">
              {selectedDate ? format(selectedDate, 'EEEE, dd MMM yyyy') : 'Select a date'}
            </span>
            {isToday(selectedDate!) && (
              <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded"
                style={{ backgroundColor: isDark ? 'rgba(59,130,246,0.2)' : '#dbeafe', color: isDark ? '#93c5fd' : '#1d4ed8' }}>
                TODAY
              </span>
            )}
          </div>
          {selectedLeaves.length > 0 && (
            <div className="ml-auto flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full"
              style={{
                backgroundColor: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2',
                color: isDark ? '#f87171' : '#b91c1c',
                border: `1px solid ${isDark ? 'rgba(239,68,68,0.3)' : '#fecaca'}`,
              }}
            >
              <Users className="w-3.5 h-3.5" />
              {selectedLeaves.length} on leave
            </div>
          )}
        </div>

        {/* Holiday banner */}
        {selectedHolidays.length > 0 && (
          <div
            className="px-5 py-2.5 text-sm font-medium flex items-center gap-2 border-b"
            style={{
              backgroundColor: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
              color: isDark ? '#f87171' : '#b91c1c',
              borderColor: isDark ? 'rgba(239,68,68,0.2)' : '#fecaca',
            }}
          >
            🎉 Holiday: {selectedHolidays.map((h: any) => h.name).join(', ')}
          </div>
        )}

        {/* Empty state */}
        {selectedLeaves.length === 0 ? (
          <div className="px-5 py-14 text-center">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
              style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#f1f5f9' }}
            >
              <Users className="w-6 h-6" style={{ color: isDark ? '#475569' : '#94a3b8' }} />
            </div>
            <p className="text-sm font-medium" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>
              No one is on leave on this day
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc', borderBottom: `1px solid ${isDark ? '#1e293b' : '#f1f5f9'}` }}>
                  {(isManager
                    ? ['Employee', 'Leave Type', 'Duration', 'Reason', 'Approved By', 'Handover To', 'Status']
                    : ['Employee', 'Leave Type', 'Duration', 'Approved By', 'Handover To', 'Status']
                  ).map(col => (
                    <th key={col} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider"
                      style={{ color: isDark ? '#64748b' : '#94a3b8' }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedLeaves.map((leave: any, idx: number) => {
                  const approver = leave.approvedBy;
                  const isApprovedByMe = approver?.id === user?.id;
                  const statusStyle = getStatusStyle(leave.status);
                  const raw = leave.leaveType?.colour ?? '#6366f1';
                  const leaveColour = raw === '#000000' || raw === '#000' ? '#6366f1' : raw;

                  return (
                    <tr
                      key={leave.id}
                      className="transition-colors"
                      style={{
                        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}`,
                        backgroundColor: idx % 2 === 1 ? (isDark ? 'rgba(255,255,255,0.015)' : 'transparent') : 'transparent',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = idx % 2 === 1 ? (isDark ? 'rgba(255,255,255,0.015)' : 'transparent') : 'transparent')}
                    >
                      {/* Employee */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                            style={{
                              backgroundColor: isDark ? 'rgba(59,130,246,0.15)' : '#dbeafe',
                              color: isDark ? '#60a5fa' : '#1d4ed8',
                            }}
                          >
                            {leave.user.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-slate-800 leading-tight">{leave.user.name}</div>
                            {leave.user.designation && (
                              <div className="text-[11px] mt-0.5" style={{ color: isDark ? '#475569' : '#94a3b8' }}>
                                {leave.user.designation}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Leave Type */}
                      <td className="px-4 py-3.5">
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-white text-xs font-semibold shadow-sm"
                          style={{ backgroundColor: leaveColour }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                          {leave.leaveType?.name}
                        </span>
                      </td>

                      {/* Duration */}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <div className="font-medium" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>
                          {format(new Date(leave.fromDate), 'dd MMM')} – {format(new Date(leave.toDate), 'dd MMM')}
                        </div>
                        <div className="text-[11px] mt-0.5" style={{ color: isDark ? '#475569' : '#94a3b8' }}>
                          {leave.duration} day{leave.duration !== 1 ? 's' : ''}
                        </div>
                      </td>

                      {/* Reason (Only for Managers) */}
                      {isManager && (
                        <td className="px-4 py-3.5 max-w-[180px]">
                          <span className="line-clamp-2 text-sm" style={{ color: isDark ? '#94a3b8' : '#475569' }}>
                            {leave.reason || '—'}
                          </span>
                        </td>
                      )}

                      {/* Approved By */}
                      <td className="px-4 py-3.5">
                        {approver ? (
                          <span className={`flex items-center gap-1.5 text-sm font-medium`}
                            style={{ color: isApprovedByMe ? (isDark ? '#60a5fa' : '#2563eb') : (isDark ? '#cbd5e1' : '#374151') }}>
                            {isApprovedByMe && <UserCheck className="w-3.5 h-3.5" />}
                            {isApprovedByMe ? 'You' : approver.name}
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: isDark ? '#475569' : '#94a3b8' }}>—</span>
                        )}
                      </td>

                      {/* Handover To */}
                      <td className="px-4 py-3.5 text-sm" style={{ color: isDark ? '#94a3b8' : '#475569' }}>
                        {leave.handoverPersonName ?? <span style={{ color: isDark ? '#475569' : '#cbd5e1' }}>—</span>}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3.5">
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold"
                          style={{
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.text,
                            border: `1px solid ${statusStyle.border}`,
                          }}
                        >
                          {statusStyle.icon}
                          {leave.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
